/**
 * MText entity → `RenderableEntity` adapter.
 *
 * AutoCAD's MTEXT carries a paragraph-formatted contents string with
 * inline DXF format codes (`\C` color, `\H` height, `\W` width factor,
 * `\T` tracking, `\Q` oblique, `\f` font, `\S` stack, `\B` background,
 * `\A` alignment, `\P` paragraph, `\~` non-breaking space, …). PR-E3
 * uses `parseMTextContent` (in
 * `apps/web/src/app/scene3d/text/mtextParser.ts`) to fully expand
 * every per-segment code into an `MTextRun`. The trivial PR-E2
 * `\P`-only splitter (`splitMTextSimple`) is kept exported for
 * regression tests that pin the legacy behaviour of the simpler path.
 *
 * # Property bag mapping
 *
 *   - `Insertion X / Y / Z`        — group codes 10/20/30.
 *   - `Height`                     — group code 40 (default 2.5).
 *   - `Rotation`                   — degrees CCW.
 *   - `Width factor`               — group code 41 (default 1).
 *   - `Line spacing factor`        — group code 44 (default 1).
 *   - `Attachment`                 — group code 71 (1..9, default 1).
 *   - `Text style`                 — STYLE name (default "Standard").
 *   - `Contents` / `entity.label`  — paragraph contents.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * Full DXF code parity is now the default — `\C`, `\H`, `\H<n>x`,
 * `\W`, `\T`, `\Q`, `\f`, `\A`, `\L/\l`, `\O/\o`, `\K/\k`, `\P`, and
 * curly-brace group scope all flow into per-run fields. `\S` and
 * `\B` are stripped (their content preserved as text in the case of
 * `\S`) and tracked for PR-E4.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import {
  readNumber,
  readPositiveNumber,
} from "../../scene/renderHelpers";
import type { MTextRun, RenderableEntity } from "../types";

import {
  readTextBagPlacement,
  type AdapterContext,
} from "./adapterContext";
import { assembleMTextRenderable } from "./geom/mtextGeometry";

/**
 * PR-E2 simple `\P`-only splitter — preserved for regression tests
 * that pin the legacy behaviour. The PR-E3 code path uses
 * `parseMTextContent` instead (see `mtextToRenderable`).
 */
export function splitMTextSimple(
  contents: string,
  styleName: string,
  heightWorld: number,
  widthFactor: number,
  colorHex: string,
): MTextRun[] {
  const lines = contents.split(/\\P/g);
  return lines.map<MTextRun>((line) => ({
    text: line,
    fontStyleName: styleName,
    colorHex,
    heightWorld,
    widthFactor,
    trackingFactor: 1,
    oblique: 0,
    bold: false,
    italic: false,
    underline: false,
    overline: false,
    strikethrough: false,
    paragraphAlign: "left",
    baselineOffsetWorld: 0,
  }));
}

/** Adapter for DXF MTEXT / `MText` entity type. */
export function mtextToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const props = entity.properties;
  const { insRaw, height, rotation: rawRotation, widthFactor } =
    readTextBagPlacement(props);
  // Codex #342 round-8 Finding A — place the glyph run at the EFFECTIVE angle the
  // producer precomputed (a custom group-11 X-axis direction WINS over the raw
  // `Rotation` attribute, ezdxf get_rotation precedence). The render-only
  // "__mtext_render_angle" key carries it; absent (older bag / non-MText) ⇒ fall
  // back to the raw rotation (which equals the effective angle for non-custom
  // MTEXT, so golden parity holds).
  const rotation = readNumber(props, "__mtext_render_angle", rawRotation);
  const lineSpacingFactor = readPositiveNumber(
    props,
    "Line spacing factor",
    1,
  );
  const attachmentRaw = readNumber(props, "Attachment", 1);
  const attachmentPoint =
    Number.isFinite(attachmentRaw) && attachmentRaw >= 1 && attachmentRaw <= 9
      ? Math.round(attachmentRaw)
      : 1;
  const styleName = (props["Text style"] ?? "Standard").trim() || "Standard";
  const contents = props.Contents ?? entity.label ?? "";
  // Codex #342 round-8 Finding B — consume the AUTHORITATIVE mirror flag the
  // producer (`buildMTextPreview`) precomputes from the FULL OCS normal via the
  // shared `isReflectedOcs` predicate (the bag carries no extrusion X/Y, so the
  // handedness one-truth is computed producer-side). Replaces the brittle
  // local `"Extrusion Z" < 0` (which mishandled `−0`/floating noise). Absent/"0"
  // ⇒ not mirrored (golden parity); a mirrored default MTEXT ⇒ "1".
  const mirrored = readNumber(props, "__mtext_render_mirrored", 0) !== 0;

  // Geometry assembly, shell, and the placeholder→resolved-colour backfill are
  // the shared one-truth in geom/mtextGeometry.ts (`assembleMTextRenderable`);
  // this adapter only supplies the values it read from the string bag.
  return assembleMTextRenderable(entity, ctx, {
    insertionRaw: insRaw,
    height,
    rotation,
    widthFactor,
    lineSpacingFactor,
    attachmentPoint,
    styleName,
    contents,
    mirrored,
  });
}
